import { normalizeProfileBio } from './normalize-profile-bio';

export const SUGGESTED_CONNECTION_AXES = ['visual', 'identity', 'coffee', 'overall'] as const;

export type SuggestedConnectionAxis = (typeof SUGGESTED_CONNECTION_AXES)[number];

/** Axis-keyed template rhyme lines — same strings as genome v2 (no per-pair LLM copy). */
export const SUGGESTED_CONNECTION_RHYME_LINES: Record<SuggestedConnectionAxis, string> = {
  visual: 'Shares your visual language.',
  identity: 'Rhymes with who you are as an artist.',
  coffee: 'Close enough to meet for coffee.',
  overall: 'Strong overall resonance with your work.',
};

export const SUGGESTED_CONNECTION_WORK_SAMPLE_COUNT = 5;

export const SUGGESTED_CONNECTION_DIGEST_CARD_COUNT = 4;

export interface SuggestedConnectionWorkSample {
  /**
   * The image id, when the caller selected it.
   *
   * Optional because the digest-email path builds samples from rows that only carry
   * urls, and an email has nothing to open. The network hub does select it, and needs
   * it to open its artwork modal on the piece that was actually tapped.
   */
  id?: string;
  thumbUrl: string;
  previewUrl: string;
}

export interface SuggestedConnectionImageSource {
  id?: string | number | null;
  thumbnailUrl?: string | null;
  optimizedUrl?: string | null;
}

export interface SuggestedConnectionCard {
  userId: string;
  slug: string | null;
  displayName: string;
  bio: string | null;
  avatarUrl: string | null;
  location: string | null;
  isHumanVerified: boolean;
  axis: SuggestedConnectionAxis;
  axisReason: string;
  workSamples: SuggestedConnectionWorkSample[];
}

export function normalizeSuggestedConnectionAxis(
  axis: SuggestedConnectionAxis | string | null | undefined
): SuggestedConnectionAxis {
  if (axis == null) return 'overall';
  const lower = String(axis).toLowerCase();
  if (SUGGESTED_CONNECTION_AXES.includes(lower as SuggestedConnectionAxis)) {
    return lower as SuggestedConnectionAxis;
  }
  return 'overall';
}

export function getSuggestedConnectionReason(axis: SuggestedConnectionAxis | string | null | undefined): string {
  return SUGGESTED_CONNECTION_RHYME_LINES[normalizeSuggestedConnectionAxis(axis)];
}

export function formatSuggestedConnectionLocation(
  city: string | null | undefined,
  country: string | null | undefined
): string | null {
  return [city, country].filter(Boolean).join(', ') || null;
}

export function workSamplesFromImages(
  images: SuggestedConnectionImageSource[] | null | undefined,
  maxCount = SUGGESTED_CONNECTION_WORK_SAMPLE_COUNT
): SuggestedConnectionWorkSample[] {
  if (!images?.length) return [];

  const samples: SuggestedConnectionWorkSample[] = [];
  for (const image of images) {
    if (samples.length >= maxCount) break;
    const thumbUrl = image.thumbnailUrl ?? image.optimizedUrl;
    if (!thumbUrl) continue;
    samples.push({
      ...(image.id == null ? {} : { id: String(image.id) }),
      thumbUrl,
      previewUrl: image.optimizedUrl ?? image.thumbnailUrl ?? thumbUrl,
    });
  }
  return samples;
}

export interface MapSuggestedConnectionCardInput {
  userId: string | number;
  slug?: string | null;
  displayName?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  city?: string | null;
  country?: string | null;
  isHumanVerified?: boolean;
  suggestedConnectionAxis?: SuggestedConnectionAxis | string | null;
  images?: SuggestedConnectionImageSource[] | null;
}

export function mapSuggestedConnectionCard(input: MapSuggestedConnectionCardInput): SuggestedConnectionCard {
  const axis = normalizeSuggestedConnectionAxis(input.suggestedConnectionAxis);
  return {
    userId: String(input.userId),
    slug: input.slug ?? null,
    displayName: input.displayName?.trim() || 'Artist',
    bio: normalizeProfileBio(input.bio),
    avatarUrl: input.avatarUrl ?? null,
    location: formatSuggestedConnectionLocation(input.city, input.country),
    isHumanVerified: Boolean(input.isHumanVerified),
    axis,
    axisReason: getSuggestedConnectionReason(axis),
    workSamples: workSamplesFromImages(input.images),
  };
}

/**
 * EMAIL FRAMINGS, BY AXIS — the same segmentation, worded for a subject line.
 *
 * `SUGGESTED_CONNECTION_RHYME_LINES` above is the card's reason line, written
 * about ONE artist in the third person ("Shares your visual language."). A
 * subject line addresses the reader about SEVERAL of them, so it needs its own
 * wordings — but the same claim, in the same vocabulary, as the `/network`
 * section heading the reader lands on
 * (`network.suggested-connections.sections.*` in the en catalog).
 *
 * Why this exists at all: "artists you may want to know" is the worst-performing
 * framing the nightly email sends — 78% of its volume, at 0.16% click. It names
 * no reason and could belong to any network. These do, and they match the page.
 *
 * Several wordings per axis because the digest's no-repeat cooldown keys on the
 * FRAMING: an inbox holding only suggestions has no other family to rotate onto,
 * so this table is the whole of its variety.
 */
export const SUGGESTED_CONNECTION_EMAIL_FRAMINGS: Record<
  SuggestedConnectionAxis,
  {
    /**
     * The `/network` section heading for this axis, verbatim. Used as the
     * subject and heading of the dedicated suggestions digest, so the email and
     * the page it opens onto carry the same sentence.
     */
    heading: string;
    /** Body label, agreeing with the number of ARTISTS: "3 artists close enough for coffee". */
    label: string;
    labelOne: string;
    emoji: string;
    /** Count wordings, for rows whose artists could not be named. */
    counted: ((n: number) => string)[];
    /**
     * Named wordings. `who` arrives already rendered ("Marta and 2 others").
     *
     * `people` is the number of members NAMED, which is what the verb has to
     * agree with — one suggestion row carries several artists, so a wording
     * that agreed with the row count read "Paula Eriya resonate with your work".
     */
    named: ((who: string, people: number) => string)[];
  }
> = {
  coffee: {
    heading: 'Close enough for coffee',
    label: 'artists close enough for coffee',
    labelOne: 'artist close enough for coffee',
    emoji: '\u2615',
    counted: [
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} close enough for coffee`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} working near your studio`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} within travelling distance`,
    ],
    named: [
      (who, people) => `${who} ${people === 1 ? 'is' : 'are'} close enough for coffee`,
      (who, people) => `${who} ${people === 1 ? 'works' : 'work'} near your studio`,
      (who) => `Close enough for coffee: ${who}`,
    ],
  },
  visual: {
    heading: 'Artists whose work rhymes with yours',
    label: 'artists whose work rhymes with yours',
    labelOne: 'artist whose work rhymes with yours',
    emoji: '\uD83D\uDD8C\uFE0F',
    counted: [
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} whose work rhymes with yours`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} working in your palette`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} sharing your surface and subject`,
    ],
    named: [
      (who, people) => `${who} ${people === 1 ? 'makes' : 'make'} work that rhymes with yours`,
      (who, people) => `${who} ${people === 1 ? 'works' : 'work'} in your palette`,
      (who) => `Work that rhymes with yours: ${who}`,
    ],
  },
  identity: {
    heading: 'Artists on a similar path',
    label: 'artists on a similar path',
    labelOne: 'artist on a similar path',
    emoji: '\uD83E\uDDED',
    counted: [
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} on a similar path`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} at the same stage as you`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} with a practice like yours`,
    ],
    named: [
      (who, people) => `${who} ${people === 1 ? 'is' : 'are'} on a similar path`,
      (who, people) => `${who} ${people === 1 ? 'is' : 'are'} at the same stage as you`,
      (who) => `On a similar path: ${who}`,
    ],
  },
  overall: {
    heading: 'Artists who resonate with your work',
    label: 'artists who resonate with your work',
    labelOne: 'artist who resonates with your work',
    emoji: '\uD83D\uDD0E',
    counted: [
      (n) => `${n} ${n === 1 ? 'artist who resonates' : 'artists who resonate'} with your work`,
      (n) => `${n} ${n === 1 ? 'artist' : 'artists'} matched to the shape of your work`,
    ],
    named: [
      (who, people) => `${who} ${people === 1 ? 'resonates' : 'resonate'} with your work`,
      (who) => `Resonating with your work: ${who}`,
    ],
  },
};

/**
 * The axis a `recommended_connections` row was matched on.
 *
 * `metadata.axis` is the axis-split shape: one row, already one axis.
 * `metadata.axes` is the per-card array the digest job writes, ordered to
 * match `recommendationIds`. A row may claim an axis only when every card
 * shares it — the same rule as the dedicated suggestions email. A null beside
 * a real axis is an unreadable card, not a second claim. Disagreement, or
 * neither field, falls to `overall`, the ranker's residual bucket and the one
 * claim true of any card.
 */
export function suggestedConnectionAxisFromMetadata(metadata: unknown): SuggestedConnectionAxis {
  if (typeof metadata !== 'object' || metadata === null) return 'overall';
  const record = metadata as Record<string, unknown>;
  if (record.axis != null) return normalizeSuggestedConnectionAxis(record.axis as string);
  if (!Array.isArray(record.axes)) return 'overall';

  const present = record.axes.reduce((memo: Set<SuggestedConnectionAxis>, axis: unknown) => {
    if (axis == null) return memo;
    return memo.add(normalizeSuggestedConnectionAxis(String(axis)));
  }, new Set<SuggestedConnectionAxis>());
  if (present.size !== 1) return 'overall';
  return [...present][0]!;
}
